import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  GridItem,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react'
import { css } from '@emotion/react'
import type { ReactElement } from 'react'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper-bundle.css'

import { makeServerProps } from '@/server/query'

import { NEW_TUTOR_ONBOARDING_TOPIC } from '@/queries/constants'
import makePage from '@/client/services/pages/makePage'
import {
  faq,
  getCertificationUrl,
  getTutorUrl,
  getVolunteerTeamCodaUrl,
} from '@/client/services/url'

import WavyHeading from '@/components/About/WavyHeading'
import FAQ from '@/components/FAQ/FAQ'
import KhanAcademyLogo from '@/components/Icons/KhanAcademyLogo'
import Layout from '@/components/Layout/Layout'
import ShwGrid from '@/components/Layout/ShwGrid'
import AboutCollegePartners from '@/components/Partners/AboutCollegePartners'
import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'

const testimonials = [
  {
    imgSrc: '/become-a-tutor/Daisy-500px.jpg',
    quote:
      'As a tutor in the supportive community at Schoolhouse, I’ve had the unique and amazing experience of learning from and ' +
      'collaborating with knowledgeable, humble, and passionate people! It’s such a joy to be able to share what I know with world-wide ' +
      'learners so conveniently and to see their progress!',
    source: 'Daisy, tutor from Canada',
  },
  {
    imgSrc: '/become-a-tutor/Michelle-500px.jpg',
    quote:
      'Tutoring has shown me how I can use knowledge I have acquired to impact a global community and I love it!',
    source: 'Michelle, tutor from Ghana',
  },
]

const howItWorks = [
  {
    title: 'Get certified and apply',
    description: () => (
      <Text>
        Before you tutor a session on Schoolhouse, you’ll need to{' '}
        <ShwLink href={getCertificationUrl('get-certified')}>get certified</ShwLink> in one or more
        topics! Get certified in high-school math topics, SAT Prep, and more. Then, fill out a short
        application to let us know a little about you!
      </Text>
    ),
  },
  {
    title: 'Get trained to tutor',
    description: () => (
      <Text>
        Once your application is approved, complete a set of trainings (on your own time!) that will
        prepare you to lead a safe, successful first session.
      </Text>
    ),
  },
  {
    title: 'Join the community',
    description: () => (
      <Text>
        As you get ready to host your first session, you’ll join our vibrant Slack community of
        tutors and connect with a peer tutor who’s here to help you get acquainted to Schoolhouse.
        They’ll guide you through the process and give you publishing privileges for your first
        session.
      </Text>
    ),
  },
  {
    title: 'Host your first session',
    description: () => (
      <Text>
        Finally, host a session! Choose from any topic you’re currently certified in and specify a
        date, time, and other details. Congrats!!
      </Text>
    ),
  },
  {
    title: 'Receive ongoing support',
    description: () => (
      <Text>
        As you continue to host sessions, you’ll receive continuous support from our community of
        tutors and mentors who are here to answer your questions, share tips and techniques, and
        support you to be the best tutor you can be.
      </Text>
    ),
  },
]

const faqItems = [
  {
    question: 'Who can become a tutor?',
    answer: () => (
      <Text>
        At Schoolhouse.world, we believe anyone can be an effective tutor–we specifically encourage
        students themselves to tutor because it’s a great way to reinforce your own learning, as
        well! For safety reasons we do NOT allow any individuals under the age of 13 to become
        tutors.
      </Text>
    ),
  },
  {
    question: 'Do I need to have tutoring or teaching experience to be a tutor?',
    answer: () => (
      <Text>
        No experience is required. While some members of our tutoring community do happen to have
        prior teaching or tutoring experience, we believe anyone can get started teaching what they
        know to others, at any stage of their journey.
      </Text>
    ),
  },
  {
    question: 'Can I get paid for tutoring?',
    answer: () => (
      <Text>
        Tutoring on Schoolhouse is currently on a volunteer-basis. We strive to lower barriers to
        receiving tutoring and make it completely free to sign up and attend any session.
      </Text>
    ),
  },
  {
    question: 'What is the time commitment for tutoring?',
    answer: () => (
      <Text>
        As a Schoolhouse.world tutor, you can set your own schedule! Choose to tutor from once a
        month to every day; you’ll be able to decide on dates and times that work for you.
      </Text>
    ),
  },
  {
    question: 'What do I do if I need help or advice?',
    answer: () => (
      <Text>
        We have your back! All tutors will go through a detailed training process, where we’ll guide
        you through the ins-and-outs of leading safe and effective sessions on Schoolhouse. When you
        become a tutor, you’ll also join our Slack community of tutors and experienced mentors who
        are all here to support you. Finally, new tutors have the opportunity to join{' '}
        {NEW_TUTOR_ONBOARDING_TOPIC} sessions where you can connect with other new tutors, practice
        using Zoom tools, and learn some best practices from seasoned Schoolhouse tutors.
      </Text>
    ),
  },
  {
    question: 'What other ways can I get involved?',
    answer: () => (
      <Text>
        In addition to volunteer tutoring opportunities, you’ll have chances to take the lead in the
        various volunteer teams across the organization. Our volunteers help review certifications,
        maintain our blog, welcome new members of the community, and much more!{' '}
        <ShwLink href={getVolunteerTeamCodaUrl()}>Learn more about volunteering here</ShwLink>.
      </Text>
    ),
  },
]

function About(): ReactElement {
  return (
    <Layout
      title="Volunteer Tutoring Opportunities | Become a Tutor "
      meta={{
        title: 'Volunteer Tutoring Opportunities  | Become a Tutor ',
        description:
          'At Schoolhouse, you can be part of a community of volunteer tutors from around the world. ' +
          'Become a tutor in math, SAT prep, and more. ' +
          'Learn more about volunteer tutoring opportunities at Schoolhouse!',
      }}
    >
      <Container>
        <ShwGrid alignItems="center" justifyItems="center" mt={8}>
          <GridItem colSpan={[12, 12, 6]}>
            <Center>
              <Box>
                <Stack
                  direction="row"
                  align="center"
                  css={css`
                    @media only screen and (max-width: 350px) {
                      display: none !important;
                    }
                  `}
                >
                  <Text as="span" variant="eyebrow">
                    From the Founder Of
                  </Text>
                  <ShwLink variant="unstyled" href="https://khanacademy.org" paddingTop="5px">
                    <KhanAcademyLogo />
                  </ShwLink>
                </Stack>
                <Heading size="3xl" as="h1" my="16px">
                  Become a Tutor with Schoolhouse&apos;s Online Tutoring Opportunities
                </Heading>
                <Text my="24px" fontSize="20px" lineHeight="28px">
                  Build your own confidence while lifting others up–volunteer to host free, virtual
                  tutoring sessions for students across the globe. Learn more about tutoring
                  opportunities at Schoolhouse!
                </Text>
                <ShwLink href={faq.getArticleUrl('become-a-tutor')}>
                  <Button variant="solid" width={['100%', 'auto']}>
                    Become a Tutor
                  </Button>
                </ShwLink>
              </Box>
            </Center>
          </GridItem>
          <GridItem colSpan={[12, 12, 6]}>
            <Center>
              <PublicImage
                src="/become-a-tutor/Hero.png"
                alt="Become a Schoolhouse.world tutor and give back to learners from around the world!"
                maxWidth="100%"
              />
            </Center>
          </GridItem>
        </ShwGrid>
      </Container>
      <Box bg="bg.info" mt={4}>
        <Container>
          <WavyHeading text="Why become a Schoolhouse tutor?" width={380} color="primary.fg" />
          <SimpleGrid
            columns={[1, 1, 2]}
            spacing="16px"
            mb={['8px', '8px', '16px']}
            alignItems="center"
          >
            <Center p="16px" gridRow={[2, 2, 1]}>
              <PublicImage
                src="/become-a-tutor/Volunteer.svg"
                alt="At Schoolhouse you can impact learners from around the world while receiving community service hours."
                maxWidth="100%"
              />
            </Center>
            <Center gridRow={1}>
              <Box textAlign="left">
                <Heading size="3xl" as="h2" mb="24px">
                  Give back through volunteering.
                </Heading>
                <Text mb="24px">
                  Volunteer your time by hosting free tutoring sessions for other students across
                  the world who need help in high-school math, SAT prep, and more. Plus, you’ll be
                  able to{' '}
                  <ShwLink href={getTutorUrl('volunteer-hours')}> track the work you do</ShwLink> as
                  volunteer and service hours for college applications. More on this below!
                </Text>
              </Box>
            </Center>
          </SimpleGrid>
          <SimpleGrid
            columns={[1, 1, 2]}
            spacing="16px"
            mb={['8px', '8px', '16px']}
            alignItems="center"
          >
            <Center>
              <Box textAlign="left">
                <Heading size="3xl" as="h2" mb="24px">
                  Build professional leadership experience.
                </Heading>
                <Text mb="24px">
                  Being a tutor helps you develop strong leadership and organizational skills–and
                  we’ll help you show off your credentials via a portfolio that summarizes it all,
                  for your school, prospective colleges, and more!
                </Text>
              </Box>
            </Center>
            <Center p="16px">
              <PublicImage
                src="/become-a-tutor/Portfolio.svg"
                alt="As a tutor, you can build a portfolio to demonstrate your mastery and leadership skills!"
                maxWidth="100%"
              />
            </Center>
          </SimpleGrid>
          <SimpleGrid
            columns={[1, 1, 2]}
            spacing="16px"
            mb={['8px', '8px', '16px']}
            alignItems="center"
          >
            <Center gridRow={[2, 2, 1]} p="16px">
              <PublicImage
                src="/become-a-tutor/Mentor.svg"
                alt="Schoolhouse.world tutors receive ongoing support from fellow tutors and mentor educators."
                maxWidth="100%"
              />
            </Center>
            <Center gridRow={1}>
              <Box textAlign="left">
                <Heading size="3xl" as="h2" mb="24px">
                  Receive support from experienced educators.
                </Heading>
                <Text mb="24px">
                  When you tutor with Schoolhouse, you join a community who has your back. Get
                  support from a network of hundreds of passionate tutors and educators who are here
                  to support you in your sessions, guide you with effective teaching techniques, and
                  help you grow.
                </Text>
              </Box>
            </Center>
          </SimpleGrid>
          <SimpleGrid columns={[1, 1, 2]} spacing="16px" alignItems="center">
            <Center>
              <Box textAlign="left">
                <Heading size="3xl" as="h2" mb="24px">
                  Make friends from all across the world.
                </Heading>
                <Text mb="24px">
                  Our diverse tutor community spans dozens of countries, hosts routine team-bonding
                  events, and feels like a huge, international family. We’re lifelong learners who
                  value connecting with people around the world and the perspective it brings–and
                  we’re excited to meet you!
                </Text>
              </Box>
            </Center>
            <Center p="16px">
              <PublicImage
                src="/home/CommunityBig.svg"
                alt="As a tutor, you can join a community of passionate volunteers from around the world."
                maxWidth="100%"
              />
            </Center>
          </SimpleGrid>
        </Container>
      </Box>
      <Container>
        <AboutCollegePartners />
      </Container>
      <Box bg="bg.subtle" mt={4}>
        <Container centerContent>
          <Text variant="eyebrow" mb={12} as="h2">
            How to become a tutor
          </Text>
          <Stack align="center" direction="column" maxWidth="800px" spacing="48px" px="16px">
            {howItWorks.map(({ title, description }, index) => (
              <Stack direction="row" spacing={8} key={title}>
                <Heading fontSize="80px" color="gray.muted" fontWeight={900}>
                  {index + 1}
                </Heading>
                <Box>
                  <Heading size="lg" as="h4" mb="16px">
                    {title}
                  </Heading>
                  {description()}
                </Box>
              </Stack>
            ))}
          </Stack>
        </Container>
      </Box>
      <Container py="48px">
        <Box textAlign="center" mb={12} mt={8}>
          <Text variant="eyebrow">Hear it From Tutors</Text>
        </Box>
        <Flex
          direction="column"
          overflow="hidden"
          flex={1}
          margin="auto"
          maxW="800px"
          css={css`
            .swiper {
              padding-bottom: 40px;
            }
            .swiper-wrapper {
              align-items: center;
            }
          `}
        >
          <Swiper
            spaceBetween={25}
            slidesPerView={1}
            pagination={{ clickable: true }}
            watchOverflow
            style={{ width: '100%' }}
            loop={testimonials.length > 1}
            loopAdditionalSlides={testimonials.length}
            modules={[Pagination]}
          >
            {testimonials.map(({ quote, imgSrc, source }) => (
              <SwiperSlide key={source}>
                <Center>
                  <Stack direction="row" spacing={4}>
                    <Center display={['none', 'flex']} flex={1}>
                      <PublicImage src={imgSrc} width="200px" />
                    </Center>
                    <Center position="relative">
                      <Box ml="40px" mt="40px">
                        <Heading size="xl" as="h3" mb="32px">
                          {quote}
                        </Heading>
                        <Text fontWeight="bold">{source}</Text>
                      </Box>
                      <Box position="absolute" left="0px" top="70px" zIndex={-1}>
                        <Text fontSize="180px" fontWeight="bold" color="yellow.emphasized">
                          “
                        </Text>
                      </Box>
                    </Center>
                  </Stack>
                </Center>
              </SwiperSlide>
            ))}
          </Swiper>
        </Flex>
      </Container>
      <Container>
        {faqItems == null ? null : (
          <Box maxWidth="800px" margin="auto" textAlign="left" mb={8}>
            <FAQ items={faqItems} category="tutoring" />
          </Box>
        )}
      </Container>
      <Box bg="bg.info">
        <Container>
          <Flex flexDir="column" textAlign="center" py={8}>
            <Heading size="2xl" mb={12}>
              Make an impact—become a Schoolhouse tutor today!
            </Heading>
            <Box>
              <ShwLink href={faq.getArticleUrl('become-a-tutor')}>
                <Button width="auto">More Info</Button>
              </ShwLink>
            </Box>
          </Flex>
        </Container>
      </Box>
    </Layout>
  )
}

const AboutPage = makePage(About, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(AboutPage)
export default AboutPage
